import { DomainPreferences } from '../../../../api/types';
import { DashboardConfig } from '../../../../common/components/dashboards/types';
import { Languages } from '../../../../constants';
import {
  dateShiftedByMonths,
  format,
  monthEnd,
} from '../../../../date/date-utils';
import {
  CalendarYearSingleValueByMonthsTimeSelection,
  TimeSelectionType,
} from '../../../../types';
import { StoryboardDateUtils } from '../../utils';
import {
  getAvgOvertimeFactbox,
  getAvgWorkHoursFactbox,
  getFemaleHeadcountFactbox,
  getFemaleManagersFactbox,
  getIndividualsFactbox,
  getIndividualsSecondedFactbox,
  getJoinersFactbox,
  getPaidLeaveFactbox,
  getVoluntaryAttritionFactbox,
} from './jtb-chart-helpers';

const customField1Filter = `EMPLOYEE.CUSTOM_FIELD_1_LEVEL_1 = 'キャリア・既卒' OR EMPLOYEE.CUSTOM_FIELD_1_LEVEL_1 = '新卒'`;

const getCurrentMonthTimeSelection = (
  storyboardDateUtils: StoryboardDateUtils
): CalendarYearSingleValueByMonthsTimeSelection => {
  const { nowEndOfMonth } = storyboardDateUtils;
  return {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: { start: nowEndOfMonth, end: nowEndOfMonth },
  };
};

const getFyYtdToCurrentMonthTimeSelection = (
  domainPreferences: DomainPreferences,
  storyboardDateUtils: StoryboardDateUtils
): CalendarYearSingleValueByMonthsTimeSelection => {
  const { nowEndOfMonth, fiscalYearStart } = storyboardDateUtils;
  return {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: {
      start: fiscalYearStart(domainPreferences),
      end: nowEndOfMonth,
    },
  };
};

const getTwoMonthsAgoTimeSelection = (
  storyboardDateUtils: StoryboardDateUtils
): CalendarYearSingleValueByMonthsTimeSelection => {
  const { now } = storyboardDateUtils;
  const twoMonthsAgoDate = monthEnd(dateShiftedByMonths(now, -2));
  return {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: { start: format(twoMonthsAgoDate), end: format(twoMonthsAgoDate) },
  };
};

const getFyYtdToTwoMonthsAgoTimeSelection = (
  domainPreferences: DomainPreferences,
  storyboardDateUtils: StoryboardDateUtils
): CalendarYearSingleValueByMonthsTimeSelection => {
  const { now, fiscalYearStart } = storyboardDateUtils;
  const twoMonthsAgoDate = monthEnd(dateShiftedByMonths(now, -2));
  return {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: {
      start: fiscalYearStart(domainPreferences, twoMonthsAgoDate),
      end: format(twoMonthsAgoDate),
    },
  };
};

const footer1 = {
  [Languages.EN]: 'Num Registered Employees at JTB Corporation',
  [Languages.JA]: '（株）JTB本籍従業員の数字',
};
const footer2 = {
  [Languages.EN]: 'Num Employees working at JTB Corporation',
  [Languages.JA]: '（株）JTBで勤務する従業員の数字',
};

export const JTBDashboardChartConfig: DashboardConfig = {
  id: 'jtb_custom_db_1',
  title: {
    [Languages.EN]: 'KPI and Related Metrics',
    [Languages.JA]: 'KPIと関連指標',
  },
  enabled: true,
  pages: [
    {
      pageId: '1',
      dashboardId: 'jtb_custom_db_1',
      pageNumber: 1,
      enabled: true,
      title: {
        [Languages.EN]: 'Page 1',
        [Languages.JA]: 'ページ１',
      },
      pageOptions: {
        displayOptions: {
          enabled: false,
        },
        timeOptions: {
          timeSliderEnabled: false,
        },
        filterTray: {
          enabled: false,
        },
        dataView: {
          enabled: false,
        },
        segmentationLevel1: {
          enabled: false,
        },
        segmentationLevel2: {
          enabled: false,
        },
        benchmark: {
          enabled: false,
        },
        forecast: {
          enabled: false,
        },
      },
      gridCols: 20,
      configType: 'componentConfigs',
      components: ({ domainPreferences, storyboardDateUtils }) => [
        getIndividualsFactbox(
          getCurrentMonthTimeSelection(storyboardDateUtils)
        ),
        getIndividualsSecondedFactbox(
          getCurrentMonthTimeSelection(storyboardDateUtils)
        ),
        getJoinersFactbox(
          getCurrentMonthTimeSelection(storyboardDateUtils),
          getFyYtdToCurrentMonthTimeSelection(
            domainPreferences,
            storyboardDateUtils
          ),
          customField1Filter
        ),
        getVoluntaryAttritionFactbox(
          getFyYtdToCurrentMonthTimeSelection(
            domainPreferences,
            storyboardDateUtils
          )
        ),
        getFemaleHeadcountFactbox(
          getCurrentMonthTimeSelection(storyboardDateUtils)
        ),
        getFemaleManagersFactbox(
          getCurrentMonthTimeSelection(storyboardDateUtils)
        ),
        getAvgWorkHoursFactbox(
          getTwoMonthsAgoTimeSelection(storyboardDateUtils)
        ),
        getAvgOvertimeFactbox(
          getTwoMonthsAgoTimeSelection(storyboardDateUtils)
        ),
        getPaidLeaveFactbox(
          getFyYtdToTwoMonthsAgoTimeSelection(
            domainPreferences,
            storyboardDateUtils
          )
        ),
      ],
    },
  ],
};
